import { useLayoutEffect, useState } from 'react';
import { Button, Caption1, Popover, PopoverSurface, PopoverTrigger, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, makeStyles, mergeClasses } from '@fluentui/react-components';
import { isComplianceTracked, languagesLine, type Person } from '@vigeo/shared';
import { StatusBadge, shellMedia, shellMetrics, tokens, useShellTier } from '@vigeo/ui';
import { PersonName, type ReadinessEntry } from '@/features/candidates';
import { ROSTER_PATH, complianceAndDocumentsShown } from '@/lib/sections';
import { statePresenters } from '@/features/readiness';
import { ComplianceHeader, LanguageHeader, NameHeader, PhoneHeader, type RosterControls } from './RosterFilterControls.js';
import { formatPhone } from './rosterFilters.js';
import { NO_PLACE_YET, PinLetter, placeWords } from '@/features/places';
import { jobTitleLine, placesOf, serviceAreaOf } from './staff.js';

/** Places shown under a name before "+N" (frame A1). The rest open from the "+N" button. */
export const PLACES_SHOWN = 2;

/** Said only while a map is on the same page as the table (`mapOnPage`): with no map the words would point at nothing (#673). */
export const LETTERS_FOOT = 'The letters beside each place are the same letters as on the map.';
export const MORE_PLACES_FOOT = '“+1”, “+2” and so on mean that many more places; click one to see them.';
export const NO_PLACES_FOOT = 'Add a place with Edit. Text typed before places existed stays on the record and shows until a place is added.';
export const NARROW_FOOT = 'Phone and email are in Edit at this width.';
export const EMAIL_FOOT = 'Email is in Edit at this width.';

const useStyles = makeStyles({
  wrapper: { backgroundColor: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadiusXLarge, boxShadow: tokens.shadowCard, minWidth: 0, overflow: 'hidden' },
  scroll: { overflowX: 'auto' },
  // Fluent tables are `table-layout: fixed`: the name column takes what the sized ones leave. The table has the width of the
  // page (#673, Figma T1 to T2b), so Places, Phone, Email, Languages and Compliance each have a column of their own and Name gets
  // the rest. Below the desktop tier Name and Places share what the sized columns leave.
  colPlaces: { width: '17.5rem', [shellMedia.belowTablet]: { width: 'auto' } },
  colPhone: { width: '7.5rem' },
  // Phone alone, with Email out of the table (the page's content under `shellMetrics.tableEmailMin`): it leaves below the desktop tier.
  colPhoneNarrow: { width: '7.5rem', [shellMedia.belowTablet]: { display: 'none' } },
  colEmail: { width: '13.5rem' },
  colLanguages: { width: '9rem' },
  colVerdict: { width: '11rem' },
  colActions: { width: '5rem' },
  headRow: { height: shellMetrics.tableHeaderRow, backgroundColor: tokens.colorNeutralBackground2 },
  headCell: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200, fontWeight: tokens.fontWeightSemibold, paddingInline: tokens.spacingHorizontalM },
  // The first column starts a little further in than the others.
  lead: { paddingInlineStart: tokens.spacingHorizontalL },
  // The Edit button is as wide as its word (frame A1), not a Fluent Button's usual minimum, so its column can be narrow.
  edit: { minWidth: 'auto' },
  // `height`, not `minHeight`: a table row ignores a minimum and takes its height as one (the header row above does the same). This
  // is what makes a row the drawing's 52 (Figma T1, `shellMetrics.tableRow`); measured, a row is 44 high with `minHeight`.
  row: { height: shellMetrics.tableRow },
  // No vertical padding is set, so cells have 0 above and below (measured). Do not rely on a `paddingBlock` here: on this page one of 8
  // or of 16 did not apply and one of 12 did (every row then 61 high). Why they differ was not established. A row that grew past 52
  // would have its text against the row's lines; none was found at 1440, 1280, 1100 or 768 wide.
  cell: { paddingInline: tokens.spacingHorizontalM, verticalAlign: 'middle' },
  wrap: { display: 'block', minWidth: 0, overflowWrap: 'anywhere' },
  // The name, and the job title in small words under it (#798).
  who: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 },
  // A phone card's first line: the name, and where compliance stands beside it (Figma T3). It drops under the name when the two do not fit.
  cardHead: { alignSelf: 'stretch', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', columnGap: tokens.spacingHorizontalM, rowGap: tokens.spacingVerticalXXS },
  cardName: { flexGrow: 1, flexShrink: 1, minWidth: 0 },
  cardVerdict: { flexShrink: 0, marginInlineStart: 'auto' },
  // A name wraps here instead of ending in an ellipsis: an ordinary name must not be cut off.
  nameLine: {
    display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: tokens.spacingHorizontalS, rowGap: tokens.spacingVerticalXXS, minWidth: 0,
    '& a': { overflow: 'visible', textOverflow: 'clip', whiteSpace: 'normal', overflowWrap: 'anywhere' },
  },
  quiet: { color: tokens.colorNeutralForeground3 },
  title: { color: tokens.colorNeutralForeground2 },
  places: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: tokens.spacingHorizontalS, rowGap: tokens.spacingVerticalXXS },
  place: { display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalXS, maxWidth: '100%', fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200, color: tokens.colorNeutralForeground2, overflowWrap: 'anywhere' },
  placeChosen: { color: tokens.colorNeutralForeground1, fontWeight: tokens.fontWeightSemibold },
  // A plain button, not a Fluent Button: the theme gives every Fluent Button the control height, which would make every row with "+N" taller.
  more: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', minWidth: '1.75rem', paddingBlock: 0, paddingInline: tokens.spacingHorizontalXS, cursor: 'pointer',
    backgroundColor: tokens.colorNeutralBackground1, color: tokens.colorNeutralForeground1, border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`, borderRadius: tokens.borderRadiusSmall,
    fontFamily: 'inherit', fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase300, fontWeight: tokens.fontWeightSemibold,
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
    ':focus-visible': { outline: `${tokens.strokeWidthThick} solid ${tokens.colorBrandForeground1}`, outlineOffset: tokens.strokeWidthThin },
    [shellMedia.phone]: { minWidth: '2.75rem', minHeight: '2.75rem' },
  },
  morePop: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, maxWidth: '16rem' },
  morePopTitle: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase200 },
  wideOnly: { [shellMedia.belowTablet]: { display: 'none' } },
  stack: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  foot: { paddingBlock: tokens.spacingVerticalM, paddingInline: tokens.spacingHorizontalL, backgroundColor: tokens.colorNeutralBackground2, borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`, color: tokens.colorNeutralForeground2 },
  card: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: tokens.spacingVerticalXS, padding: tokens.spacingHorizontalL, backgroundColor: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadiusXLarge, boxShadow: tokens.shadowCard },
});

export interface StaffTableProps {
  readonly people: readonly Person[];
  readonly entryOf: (id: string) => ReadinessEntry;
  /** Offered only to someone who may edit; absent for a viewer. */
  readonly onEdit?: (person: Person, opener: HTMLElement) => void;
  /** The letter of each place, A to Z by its name as shown: the letters the map's pins carry. Absent, or no letter: the place has none. */
  readonly letterOf?: (placeId: string) => string | null;
  /** The places the roster is filtered to: they read in bold in each person's places (frame A2). */
  readonly chosenPlaces?: readonly string[];
  /** The filter and sort controls the column headers share with the filter row. Absent: plain headers. */
  readonly controls?: RosterControls;
  /**
   * A click on a name opens a quick look over the Roster (#585) and is handed the name, so the keyboard can go back to it.
   * `profileHref` is where the name leads as a link. Absent, it leads to the employee profile all the same: never to the candidate record (#779).
   */
  readonly onOpen?: (person: Person, opener: HTMLElement) => void;
  readonly profileHref?: (person: Person) => string;
  /**
   * Whether the page the table is on has a map. Only then does the foot line say the letters are the map's. The Roster turns it
   * on now that its Map view is one click away (#689); a page with no map leaves it off, and the words would point at nothing.
   */
  readonly mapOnPage?: boolean;
}

/**
 * Whether `element` is at least `min` wide, kept up to date as it changes (the window resized, the menu widened). What matters is the
 * width of the PAGE's content, which the window alone does not give: the menu is 248 or 56 wide and the person can widen it.
 *
 * An element with no layout (a test with no browser, or an element not shown) reads as wide, so the full set of columns is the
 * default and nothing is hidden by a width nobody measured. The first measure is taken before the first paint, so there is no
 * moment of the wrong layout.
 */
export function useAtLeastWide(min: number): readonly [(element: HTMLElement | null) => void, boolean] {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [wide, setWide] = useState(true);
  useLayoutEffect(() => {
    if (element === null) return undefined;
    const measure = () => {
      const width = element.getBoundingClientRect().width;
      setWide(width === 0 || width >= min);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element, min]);
  return [setElement, wide];
}

export interface PlacesProps {
  readonly person: Person;
  readonly letterOf: ((placeId: string) => string | null) | undefined;
  readonly chosenPlaces: readonly string[];
}

/**
 * A person's places as labels: each place by name with its pin's letter. They are the Places column of the table (#632) and,
 * on a phone, the line under the name on a person's card. With no place it reads "No place yet", and the text typed before
 * places existed shows after it until a place is added (ADR-018 §1: the typed text stays on the record). Each name is read from
 * the app's own list of places by the place's code (ADR-021, #594), with no request; a code that is not in the list reads
 * "Pick this place again". The labels wrap inside whatever width they are given, so a person with many places has a taller row.
 */
export function PlacesLine({ person, letterOf, chosenPlaces }: PlacesProps) {
  const styles = useStyles();
  const places = placesOf(person);
  if (places.length === 0) {
    const typed = serviceAreaOf(person);
    return <Caption1 className={mergeClasses(styles.quiet, styles.wrap)}>{typed === null ? NO_PLACE_YET : `${NO_PLACE_YET} · typed before: ${typed}`}</Caption1>;
  }
  const chip = (place: (typeof places)[number]) => (
    <span key={place.placeId} className={mergeClasses(styles.place, chosenPlaces.includes(place.placeId) && styles.placeChosen)}>
      <PinLetter letter={letterOf?.(place.placeId) ?? null} small />
      {placeWords(place.placeId)}
    </span>
  );
  // A chosen place is always one of the ones shown, so the reason a person is in the list is never behind "+N".
  const ordered = [...places].sort((a, b) => Number(chosenPlaces.includes(b.placeId)) - Number(chosenPlaces.includes(a.placeId)));
  const rest = ordered.slice(PLACES_SHOWN);
  const who = `${person.name.givenName} ${person.name.familyName}`;
  return (
    <span className={styles.places}>
      {ordered.slice(0, PLACES_SHOWN).map(chip)}
      {rest.length === 0 ? null : (
        // Opens under the button; Esc closes it when the button has the keyboard. NOT on the popover rules (PG11): after a click outside the keyboard is left on the page (#542 review).
        <Popover positioning="below-start" trapFocus={false}>
          <PopoverTrigger disableButtonEnhancement>
            <button type="button" className={styles.more} aria-label={`Show ${rest.length} more ${rest.length === 1 ? 'place' : 'places'} for ${who}`}>+{rest.length}</button>
          </PopoverTrigger>
          <PopoverSurface>
            <div className={styles.morePop}>
              <span className={styles.morePopTitle}>Other places for {who}</span>
              <span className={styles.places}>{rest.map(chip)}</span>
              <Caption1 className={styles.quiet}>Change places in Edit.</Caption1>
            </div>
          </PopoverSurface>
        </Popover>
      )}
    </span>
  );
}

/** The compliance column: a derived readiness for tracked people, and a plain statement for the rest (ADR-016 §4). */
export function Compliance({ person, entryOf }: { readonly person: Person; readonly entryOf: (id: string) => ReadinessEntry }) {
  const styles = useStyles();
  if (!isComplianceTracked(person)) return <Caption1 className={styles.quiet}>Not tracked yet</Caption1>;
  const entry = entryOf(person.id);
  if (entry.status === 'ready') {
    const presenter = statePresenters[entry.checklist.state];
    return <StatusBadge tone={presenter.tone}>{presenter.label}</StatusBadge>;
  }
  return <Caption1 className={styles.quiet}>{entry.status === 'pending' ? 'Deriving…' : 'Unavailable'}</Caption1>;
}

/** The name with Inactive as a badge beside it (the roster holds active and inactive people only). */
export function NameLine({ person, compact = false, onOpen, profileHref }: Pick<PlacesProps, 'person'> & Pick<StaffTableProps, 'onOpen' | 'profileHref'> & { readonly compact?: boolean }) {
  const styles = useStyles();
  return (
    <div className={styles.nameLine}>
      <PersonName person={person} compact={compact} to={profileHref?.(person) ?? `${ROSTER_PATH}/${person.id}`} onOpen={onOpen} />
      {person.lifecycle === 'inactive' ? <StatusBadge tone="neutral">Inactive</StatusBadge> : null}
    </div>
  );
}

/** The person's job title in small words, under the name (#798). "No role set" reads quieter, as the empty cells do. */
export function JobTitle({ person }: Pick<PlacesProps, 'person'>) {
  const styles = useStyles();
  const empty = person.jobProfileKeys.length === 0;
  return <Caption1 data-job-title="" className={mergeClasses(styles.wrap, empty ? styles.quiet : styles.title)}>{jobTitleLine(person)}</Caption1>;
}

/**
 * The staff table (Figma T1 to T3, #673): name with the job title under it, places, phone, email, languages, and where compliance
 * stands, across the page's whole width. Languages and its heading filter are drawn at every width the table is (#798). Email is
 * the first to go: under `shellMetrics.tableEmailMin` of content it leaves the table, and Phone, alone, also leaves below the
 * desktop tier. On a phone each person is a card with compliance beside the name and the job title under it.
 */
export function StaffTable({ people, entryOf, onEdit, letterOf, chosenPlaces = [], controls, onOpen, profileHref, mapOnPage = false }: StaffTableProps) {
  const styles = useStyles();
  const tier = useShellTier();
  /** Not drawn where compliance is not tracked and no file can be held (#723): there is no column, no filter on it, and no cell. */
  const showCompliance = complianceAndDocumentsShown();
  // Compliance takes 11rem, so with it drawn Email needs a wider page to leave Name its room.
  const [measure, showEmail] = useAtLeastWide(showCompliance ? shellMetrics.tableEmailMin : shellMetrics.tableEmailMinNoCompliance);
  /**
   * `inTable` gives the button the field height (36) instead of the theme's control height (40): the drawing's button is smaller than a
   * form control. It does not decide the row, which is 52 because of `height` on the row (measured: 52 with the button at 40 too).
   * The theme's minimum is a rule a class cannot beat, so it is set on the button itself; a card on a phone keeps the touch size.
   */
  const edit = (person: Person, inTable = false) => onEdit === undefined ? null : <Button size="medium" appearance="secondary" className={styles.edit} style={inTable ? { minHeight: shellMetrics.fieldHeight } : undefined} onClick={(event) => onEdit(person, event.currentTarget)} aria-label={`Edit ${person.name.givenName} ${person.name.familyName}`}>Edit</Button>;
  const places = (person: Person): PlacesProps => ({ person, letterOf, chosenPlaces });

  if (tier === 'mobile') {
    return (
      <div className={styles.stack}>
        <div className={styles.stack} role="list" aria-label="Staff">
          {people.map((person) => (
            <div key={person.id} role="listitem" className={styles.card}>
              <div className={styles.cardHead}>
                <div className={styles.cardName}><NameLine person={person} onOpen={onOpen} profileHref={profileHref} /><JobTitle person={person} /></div>
                {showCompliance ? <div className={styles.cardVerdict}><Compliance person={person} entryOf={entryOf} /></div> : null}
              </div>
              <PlacesLine {...places(person)} />
              <Caption1 className={styles.quiet}>{[formatPhone(person.contact.phone), person.contact.email].filter((v) => v !== '').join(' · ')}</Caption1>
              {edit(person)}
            </div>
          ))}
        </div>
      </div>
    );
  }

  const anyPlace = people.some((person) => placesOf(person).length > 0);
  // Phone is out of the table only while Email is AND the window is under the desktop tier.
  const phoneHidden = !showEmail && tier === 'tablet';
  const foot = [
    !anyPlace ? NO_PLACES_FOOT : '',
    anyPlace && mapOnPage ? LETTERS_FOOT : '',
    people.some((person) => placesOf(person).length > PLACES_SHOWN) ? MORE_PLACES_FOOT : '',
    phoneHidden ? NARROW_FOOT : !showEmail ? EMAIL_FOOT : '',
  ].filter((sentence) => sentence !== '').join(' ');

  return (
    <div className={styles.wrapper} ref={measure}>
      <div className={styles.scroll}>
        <Table size="medium" aria-label="Staff">
          <colgroup>
            <col />
            <col className={styles.colPlaces} />
            <col className={showEmail ? styles.colPhone : styles.colPhoneNarrow} />
            {showEmail ? <col className={styles.colEmail} /> : null}
            <col className={styles.colLanguages} />
            {showCompliance ? <col className={styles.colVerdict} /> : null}
            <col className={styles.colActions} />
          </colgroup>
          <TableHeader>
            <TableRow className={styles.headRow}>
              <TableHeaderCell className={mergeClasses(styles.headCell, styles.lead)} aria-sort={controls === undefined ? undefined : controls.sort === 'asc' ? 'ascending' : 'descending'}>{controls === undefined ? 'Name' : <NameHeader controls={controls} />}</TableHeaderCell>
              {/* Not sorted and not a filter of its own: the Place field above already answers "who is in this place". */}
              <TableHeaderCell className={styles.headCell}>Places</TableHeaderCell>
              {/* The phone filter (Has a phone, No phone) sits on Phone; Email has none of its own. */}
              <TableHeaderCell className={mergeClasses(styles.headCell, !showEmail && styles.wideOnly)}>{controls === undefined ? 'Phone' : <PhoneHeader controls={controls} column="Phone" />}</TableHeaderCell>
              {showEmail ? <TableHeaderCell className={styles.headCell}>Email</TableHeaderCell> : null}
              <TableHeaderCell className={styles.headCell}>{controls === undefined ? 'Languages' : <LanguageHeader controls={controls} />}</TableHeaderCell>
              {showCompliance ? <TableHeaderCell className={styles.headCell}>{controls === undefined ? 'Compliance' : <ComplianceHeader controls={controls} />}</TableHeaderCell> : null}
              <TableHeaderCell className={styles.headCell} aria-label="Actions" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {people.map((person) => {
              const phone = formatPhone(person.contact.phone);
              const languages = languagesLine(person.languages);
              return (
                <TableRow key={person.id} className={styles.row}>
                  <TableCell className={mergeClasses(styles.cell, styles.lead)}>
                    <div className={styles.who}><NameLine person={person} onOpen={onOpen} profileHref={profileHref} compact /><JobTitle person={person} /></div>
                  </TableCell>
                  <TableCell className={styles.cell}><PlacesLine {...places(person)} /></TableCell>
                  <TableCell className={mergeClasses(styles.cell, !showEmail && styles.wideOnly)}><Caption1 className={mergeClasses(styles.wrap, phone === '' ? styles.quiet : undefined)}>{phone === '' ? '—' : phone}</Caption1></TableCell>
                  {showEmail ? <TableCell className={styles.cell}><Caption1 className={styles.wrap}>{person.contact.email}</Caption1></TableCell> : null}
                  <TableCell className={styles.cell}><Caption1 className={mergeClasses(styles.wrap, languages === null ? styles.quiet : undefined)}>{languages ?? '—'}</Caption1></TableCell>
                  {showCompliance ? <TableCell className={styles.cell}><Compliance person={person} entryOf={entryOf} /></TableCell> : null}
                  <TableCell className={styles.cell}>{edit(person, true)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {foot === '' ? null : <div className={styles.foot}><Caption1>{foot}</Caption1></div>}
    </div>
  );
}
